import {
  Box,
  Container,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { PageTitleBar } from "common/components";
import { useTranslation } from "common/locales";
import GenericDropdown from "features/general/GenericDropdown";
import { useAppDispatch, useAppSelector } from "model";
import { selectAllFarmerProgrammeDashboards } from "model/farmerDashboardsSlice";
import { getMetabaseUrl } from "model/metabaseUrlSlice";
import { setSelectedFarmerDashboard } from "model/selectedFarmerDashboard";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

export default function FarmInsightsDrawer() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const farmerDashboards = useAppSelector(selectAllFarmerProgrammeDashboards);
  const selectedDashboard = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
    (state) => (state as any).selectedFarmerDashboard
  );
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [metabaseUrl, setMetabaseUrl] = useState<string>("");
  const user = useAppSelector((state) => state.user.details);

  const farmerDashboardsAsMap: { [key: string]: string } = {};
  farmerDashboards.forEach((dashboard) => {
    farmerDashboardsAsMap[dashboard.guid] = dashboard.title;
  });

  // Load Metabase URL when dashboard is selected (mobile only)
  useEffect(() => {
    if (isMobile && selectedDashboard && selectedDashboard.guid) {
      const workaroundFunc = async () => {
        const params: { [name: string]: string } = {};
        try {
          const res = await dispatch(
            getMetabaseUrl({
              visualizationId: selectedDashboard.guid,
              params,
            })
          ).then(unwrapResult);
          setMetabaseUrl(res.url);
        } catch (e) {
          console.log(e);
        }
      };
      void workaroundFunc();
    }
  }, [dispatch, selectedDashboard, user?.mail, user?.name, isMobile]);

  if (!selectedDashboard.guid && farmerDashboards.length > 0) {
    // Default to the first dashboard if none is selected
    const firstDashboard = farmerDashboards[0];
    if (firstDashboard) {
      dispatch(
        setSelectedFarmerDashboard({
          guid: firstDashboard.guid,
          title: firstDashboard.title,
        })
      );
    }
  }

  // If no farm dashboards, show clickable message
  if (farmerDashboards.length === 0) {
    return (
      <Container
        maxWidth="sm"
        disableGutters
        sx={{
          pt: isMobile ? 2 : 0,
          paddingTop: isMobile
            ? "calc(env(safe-area-inset-top) + 16px)"
            : undefined,
          height: isMobile ? "calc(100vh - 120px)" : "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            p: 4,
          }}
        >
          <Typography
            variant="body1"
            color="text.secondary"
            onClick={() => history.push("/programmes")}
            sx={{
              cursor: "pointer",
              textDecoration: "underline",
              "&:hover": {
                color: "primary.main",
              },
            }}
          >
            Sign up to a programme to gain more insights into your farm!
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <>
      <PageTitleBar title={t("go.to.farm.insights")} showDivider />
      <DashboardListContainer
        maxWidth="sm"
        disableGutters
        sx={{
          pb: 2, // Add bottom padding for mobile bottom navigation
          paddingTop: isMobile ? "calc(env(safe-area-inset-top) + 16px)" : 3,
          height: isMobile ? "calc(100vh - 120px)" : "auto", // Increased height - less space reserved
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Grid item xs={12} sm={6} display="flex" alignItems="baseline">
          <GenericDropdown
            required={false}
            disabled={false}
            flipKeyValue={true}
            onChange={(event) => {
              const selectedDashboard = farmerDashboards.filter(
                (d) => d.guid === event.target.value
              )[0];

              dispatch(setSelectedFarmerDashboard(selectedDashboard));

              // Clear the previous Metabase URL when selecting a new dashboard
              if (isMobile) {
                setMetabaseUrl("");
              }
            }}
            currentValue={selectedDashboard.guid}
            label={"farmer.dashboard.label"}
            name={`selectedFarmerDashboard`}
            possibleValues={farmerDashboardsAsMap}
            tPrefix={"reseedEvent.totalAmountUnit.label"}
            inputError={false}
            shouldTranslate={false}
          />
        </Grid>

        {/* Mobile Dashboard iframe */}
        {isMobile && (
          <Grid
            item
            xs={12}
            sx={{ flex: 1, display: "flex", flexDirection: "column" }}
          >
            {metabaseUrl ? (
              <Box
                sx={{
                  backgroundColor: "white",
                  borderRadius: 2,
                  boxShadow: 1,
                  overflow: "hidden",
                  flex: 1, // Take remaining space
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <iframe
                  frameBorder="0"
                  src={metabaseUrl}
                  width="100%"
                  height="100%"
                  title="Farm-insights"
                  style={{ border: "none", flex: 1 }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  backgroundColor: "white",
                  borderRadius: 2,
                  p: 3,
                  boxShadow: 1,
                  flex: 1, // Take remaining space
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "calc(100vh - 300px)", // Ensure minimum height even when loading
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  Loading dashboard...
                </Typography>
              </Box>
            )}
          </Grid>
        )}
      </DashboardListContainer>
    </>
  );
}

const DashboardListContainer = styled(Container)``;
